/**
 * Parses optional `workspace_config.structured_analytics` overrides.
 * Backend will populate these fields once Text-to-SQL runtime APIs land.
 */

export type TextToSqlReadinessState = 'needs_setup' | 'configured' | 'draft_pending' | 'approved' | 'blocked';

export interface StructuredAnalyticsDraft {
  allowlist: StructuredAssetAllowlistRow[];
  readiness: TextToSqlReadinessState;
  activeRuntimePackVersion: string | null;
  ontologyVersionLabel: string | null;
  warehouseLabel: string | null;
  warehouseStatus: 'missing' | 'connected' | 'stale';
  catalogLabel: string | null;
  catalogIndexStatus: 'missing' | 'healthy' | 'stale' | 'partial';
  catalogCoverageHint: string;
  selectedAssetCount: number;
  allowlistRows: StructuredAssetAllowlistRow[];
  catalogRows: WorkspaceCatalogCoverageRow[];
  ontology: WorkspaceOntology;
  blockers: StructuredReadinessBlocker[];
}

export type StructuredReadinessActionTarget =
  | 'warehouse'
  | 'assets'
  | 'ontology'
  | 'runtime_pack'
  | 'catalog'
  | 'none';

export interface StructuredReadinessBlocker {
  code?: string;
  severity: 'error' | 'warning';
  message: string;
  actionLabel?: string;
  actionTarget?: StructuredReadinessActionTarget;
}

export interface WorkspaceOntology {
  entities: OntologyEntity[];
  semanticMappings?: OntologySemanticMapping[];
  metrics: OntologyMetric[];
  joins: OntologyJoinPath[];
}

export interface OntologyEntity {
  id: string;
  name: string;
  physicalTable: string;
  sourceBindings?: OntologyEntitySourceBinding[];
  description: string;
  synonyms: string[];
  ontologyRef?: string | null;
  catalogAssetId?: number | null;
}

export interface OntologyEntitySourceBinding {
  tableId: string;
  bindingRole?: string | null;
  sourceAuthority?: string | null;
  identityColumns: unknown[];
  identityMappings?: OntologyIdentityMapping[];
  normalizationRules: Array<Record<string, unknown>>;
  matchMethod?: string | null;
  matchEvidence: Array<Record<string, unknown>>;
  grain?: Record<string, unknown> | null;
  evidence?: Record<string, unknown> | unknown[] | null;
  confidence: number;
  reviewState: string;
  executionMode: string;
  trustContract?: OntologyBindingTrustContract | null;
  conflictState?: string;
  conflicts: unknown[];
}

export interface OntologyIdentityMapping {
  mappingType: 'exact' | 'normalized' | 'lookup_bridge' | 'value_overlap' | 'probabilistic';
  localColumnIds: number[];
  localColumnNames: string[];
  counterpartTableId?: number | null;
  counterpartColumnIds: number[];
  counterpartColumnNames: string[];
  normalizationRules: Array<Record<string, unknown>>;
  evidenceRefs: string[];
  confidence: number;
}

export interface OntologyBindingTrustContract {
  authorityTier: 'system_of_record' | 'authoritative' | 'supporting' | 'derived' | 'unknown';
  permittedUses: Array<'identity_resolution' | 'enrichment' | 'filtering' | 'aggregation' | 'display'>;
  conflictPolicy: 'primary_wins' | 'highest_confidence' | 'steward_required' | 'reject';
  requiresStewardReview: boolean;
  notes?: string | null;
}

export interface OntologySemanticMapping {
  id: string;
  conceptKey: string;
  label: string;
  entityId: string;
  physicalTable: string;
  columnId: string;
  description: string;
  aliases: string[];
  defaultAggregation?: string | null;
  valueKind?: string | null;
  catalogAssetId?: number | null;
  semanticMetadata?: Record<string, unknown> | null;
}

export interface OntologyMetric {
  id: string;
  name: string;
  entityId: string;
  expression: string;
  description: string;
  primaryColumnId?: string | null;
  expressionMetadata?: Record<string, unknown> | null;
}

export interface OntologyJoinPath {
  id: string;
  name?: string | null;
  leftEntityId: string;
  rightEntityId: string;
  joinCondition: string;
  relationshipKind?: 'semantic_only' | 'join_backed' | 'computed' | string | null;
  predicate?: string | null;
  inversePredicate?: string | null;
  sourceRole?: string | null;
  targetRole?: string | null;
  optionality?: 'required' | 'optional' | 'unknown' | string | null;
  description: string;
  sourceTableId?: string | null;
  targetTableId?: string | null;
  joinConditions?: Array<Record<string, unknown>> | null;
  joinEvidenceStatus?: string | null;
  traversalAllowed?: boolean | null;
  cardinality?: string | null;
  cardinalityWarning?: string | null;
  status?: string | null;
}

export interface StructuredAssetAllowlistRow {
  id: string;
  dataset: string;
  objectName: string;
  objectType: 'table' | 'view';
  rowEstimate: string;
  freshness: string;
  catalogBadge: 'matched' | 'missing' | 'ambiguous' | 'stale';
  certified: boolean;
  selected: boolean;
}

export interface WorkspaceCatalogCoverageRow {
  id: string;
  qualifiedName: string;
  resolverState: 'resolved' | 'ambiguous' | 'missing' | 'stale';
  ownerHint: string | null;
  sourceHint?: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function coerceReadiness(raw: unknown): TextToSqlReadinessState | null {
  const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (
    s === 'needs_setup' ||
    s === 'configured' ||
    s === 'draft_pending' ||
    s === 'approved' ||
    s === 'blocked'
  ) {
    return s;
  }
  return null;
}

function mapAllowRow(raw: unknown): StructuredAssetAllowlistRow | null {
  const o = asRecord(raw);
  if (!o?.id || !o.dataset || !o.objectName) return null;
  const catalog = String(o.catalogBadge || o.atlanBadge || '').toLowerCase();
  const ot = String(o.objectType || 'table').toLowerCase();
  return {
    id: String(o.id),
    dataset: String(o.dataset),
    objectType: ot === 'view' ? 'view' : 'table',
    objectName: String(o.objectName),
    rowEstimate: String(o.rowEstimate ?? '—'),
    freshness: String(o.freshness ?? '—'),
    catalogBadge:
      catalog === 'missing' || catalog === 'ambiguous' || catalog === 'stale' ? catalog : 'matched',
    certified: Boolean(o.certified),
    selected: o.selected !== false,
  };
}

function mapCatalogRow(raw: unknown): WorkspaceCatalogCoverageRow | null {
  const o = asRecord(raw);
  if (!o?.id || !o.qualifiedName) return null;
  const r = String(o.resolverState || 'resolved').toLowerCase();
  const resolverState =
    r === 'ambiguous' || r === 'missing' || r === 'stale' ? r : 'resolved';
  return {
    id: String(o.id),
    qualifiedName: String(o.qualifiedName),
    resolverState,
    ownerHint: o.ownerHint != null ? String(o.ownerHint) : null,
    sourceHint: o.sourceHint != null ? String(o.sourceHint) : null,
  };
}

function mapBlocker(raw: unknown): StructuredReadinessBlocker | null {
  const o = asRecord(raw);
  if (!o?.message) return null;
  const sev = String(o.severity || 'warning').toLowerCase();
  return {
    code: typeof o.code === 'string' ? o.code : undefined,
    severity: sev === 'error' ? 'error' : 'warning',
    message: String(o.message),
  };
}

const DEFAULT_ALLOWLIST: StructuredAssetAllowlistRow[] = [
  {
    id: 'demo-orders',
    dataset: 'finance_prod',
    objectName: 'orders_fact',
    objectType: 'table',
    rowEstimate: '128k rows',
    freshness: 'Synced 45m ago',
    catalogBadge: 'matched',
    certified: true,
    selected: true,
  },
  {
    id: 'demo-arr',
    dataset: 'finance_prod',
    objectName: 'arr_rollforward_v',
    objectType: 'view',
    rowEstimate: 'Estimate',
    freshness: 'Derived',
    catalogBadge: 'ambiguous',
    certified: false,
    selected: true,
  },
  {
    id: 'demo-exp',
    dataset: 'ops_staging',
    objectName: 'experiment_events',
    objectType: 'table',
    rowEstimate: '4.3M rows',
    freshness: 'Synced 24h ago',
    catalogBadge: 'missing',
    certified: false,
    selected: false,
  },
];

const DEFAULT_CATALOG_ROWS: WorkspaceCatalogCoverageRow[] = [
  {
    id: 'c1',
    qualifiedName: 'bq:finance_prod.orders_fact',
    resolverState: 'resolved',
    ownerHint: 'Finance',
  },
  {
    id: 'c2',
    qualifiedName: 'bq:finance_prod.arr_rollforward_v',
    resolverState: 'ambiguous',
    ownerHint: 'Finance',
  },
  {
    id: 'c3',
    qualifiedName: 'bq:ops_staging.experiment_events',
    resolverState: 'missing',
    ownerHint: null,
  },
];

/** Derives structured analytics UI model from workspace config. */
export function deriveStructuredAnalyticsDraft(
  workspaceConfig: Record<string, unknown> | null | undefined,
): StructuredAnalyticsDraft {
  const root = asRecord(workspaceConfig?.structured_analytics);
  const readiness = coerceReadiness(root?.readiness) ?? 'needs_setup';

  const allowRaw = Array.isArray(root?.allowlistRows) ? root?.allowlistRows : null;
  const allowlistRows =
    allowRaw?.map(mapAllowRow).filter((row): row is StructuredAssetAllowlistRow => Boolean(row)) ??
    (readiness === 'needs_setup' ? [] : DEFAULT_ALLOWLIST);

  const catRaw = Array.isArray(root?.catalogRows) ? root?.catalogRows : null;
  const catalogRows =
    catRaw?.map(mapCatalogRow).filter((row): row is WorkspaceCatalogCoverageRow => Boolean(row)) ??
    (readiness === 'needs_setup' ? [] : DEFAULT_CATALOG_ROWS);

  const blockRaw = Array.isArray(root?.blockers) ? root?.blockers : null;
  const parsedBlockers =
    blockRaw?.map(mapBlocker).filter((b): b is StructuredReadinessBlocker => Boolean(b)) ?? [];

  const fallbackBlockers = (): StructuredAnalyticsDraft['blockers'] => {
    if (readiness === 'needs_setup') {
      return [
        {
          severity: 'warning' as const,
          message:
            'Link a warehouse connection and pick approved assets before Text-to-SQL can run in Agent Mesh.',
        },
      ];
    }
    if (readiness === 'draft_pending') {
      return [
        {
          severity: 'warning' as const,
          message: 'Ontology draft is waiting for reviewer approval — queries stay gated until activation.',
        },
      ];
    }
    if (readiness === 'blocked') {
      return [
        {
          severity: 'error' as const,
          message:
            'Schema drift on `finance_prod.orders_fact.order_ts`. Resolve registry impact before recompiling the Runtime Context Pack.',
        },
      ];
    }
    return [];
  };

  const blockers = parsedBlockers.length > 0 ? parsedBlockers : fallbackBlockers();

  const selectedAssetCount =
    typeof root?.selectedAssetCount === 'number'
      ? root.selectedAssetCount
      : allowlistRows.filter((r) => r.selected).length;

  return {
    allowlist: allowlistRows,
    readiness,
    activeRuntimePackVersion:
      typeof root?.activeRuntimePackVersion === 'string'
        ? root.activeRuntimePackVersion
        : readiness === 'approved'
          ? 'pack-2026.05.b12'
          : null,
    ontologyVersionLabel:
      typeof root?.ontologyVersionLabel === 'string' ? root.ontologyVersionLabel : 'ontology/v0 (draft)',
    warehouseLabel:
      typeof root?.warehouseLabel === 'string' ? root.warehouseLabel : null,
    warehouseStatus:
      root?.warehouseStatus === 'missing' ||
        root?.warehouseStatus === 'connected' ||
        root?.warehouseStatus === 'stale'
        ? root.warehouseStatus
        : readiness === 'needs_setup'
          ? 'missing'
          : 'connected',
    catalogLabel:
      typeof root?.catalogLabel === 'string' ? root.catalogLabel : 'Eliza Platform Catalog',
    catalogIndexStatus:
      root?.catalogIndexStatus === 'missing' ||
        root?.catalogIndexStatus === 'healthy' ||
        root?.catalogIndexStatus === 'stale' ||
        root?.catalogIndexStatus === 'partial'
        ? root.catalogIndexStatus
        : readiness === 'needs_setup'
          ? 'missing'
          : readiness === 'draft_pending'
            ? 'partial'
            : readiness === 'blocked'
              ? 'stale'
              : 'healthy',
    catalogCoverageHint:
      typeof root?.catalogCoverageHint === 'string'
        ? root.catalogCoverageHint
        : 'Coverage is derived from your selected warehouse assets. Fix ambiguous links without changing warehouse scope.',
    selectedAssetCount,
    allowlistRows,
    catalogRows,
    ontology: root?.ontology ? (root.ontology as WorkspaceOntology) : {
      entities: [
        { id: 'e1', name: 'Order', physicalTable: 'finance_prod.orders_fact', description: 'Main sales transactions', synonyms: ['transaction', 'sale', 'purchase'] },
        { id: 'e2', name: 'Customer', physicalTable: 'finance_prod.customers', description: 'Customer profiles', synonyms: ['client', 'user', 'account'] }
      ],
      semanticMappings: [
        {
          id: 'sm1',
          conceptKey: 'order.total_revenue',
          label: 'Total Revenue',
          entityId: 'e1',
          physicalTable: 'finance_prod.orders_fact',
          columnId: 'amount',
          description: 'Revenue business meaning mapped to order amount.',
          aliases: ['revenue', 'sales'],
          defaultAggregation: 'sum',
          valueKind: 'measure',
        },
      ],
      metrics: [
        { id: 'm1', name: 'Total Revenue', entityId: 'e1', expression: 'SUM(amount)', description: 'Gross sales amount', expressionMetadata: { sql_template: 'SUM({value})', inputs: { value: { semantic_mapping_concept_key: 'order.total_revenue' } } } },
        { id: 'm2', name: 'Active Users', entityId: 'e2', expression: 'COUNT(DISTINCT customer_id)', description: 'Users with at least one order' }
      ],
      joins: [
        { id: 'j1', leftEntityId: 'e1', rightEntityId: 'e2', joinCondition: 'orders_fact.customer_id = customers.id', description: 'Orders to Customers mapping' }
      ]
    },
    blockers,
  };
}
